import React from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import dayjs from 'dayjs';

const ENROLLMENT_TYPES = [
  { label: 'Grados', value: 'grade', endpoint: 'grade-enrollments' },
  { label: 'Carreras Tecnicas', value: 'technical_career', endpoint: 'technical-career-enrollments' },
  { label: 'Cursos', value: 'course', endpoint: 'course-enrollments' },
];

export default function CreateEnrollmentDrawer({ isOpen, onClose, studentId, onSuccess }) {
  const toast = useToast();
  const { user } = useAuth();
  const schoolId = user?.school?.id;

  const [schoolPrograms, setSchoolPrograms] = React.useState([]);
  const [fetchingData, setFetchingData] = React.useState(false);

  const [enrollmentType, setEnrollmentType] = React.useState('');
  const [selectedProgramType, setSelectedProgramType] = React.useState('');
  const [selectedProgram, setSelectedProgram] = React.useState('');
  const [semesters, setSemesters] = React.useState([]);
  const [selectedSemester, setSelectedSemester] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [fetchingSemesters, setFetchingSemesters] = React.useState(false);

  // Fetch school-programs and semesters when modal opens
  React.useEffect(() => {
    if (!isOpen || !schoolId) return;

    const fetchData = async () => {
      try {
        setFetchingData(true);
        const [programsRes, semRes] = await Promise.all([
          xhr(
            `/school-programs?filters[school][id][$eq]=${schoolId}&filters[active][$eq]=true&populate=program_type,grade,technical_career,course&pagination[pageSize]=200`
          ),
          xhr(`/semesters?filters[school][id][$eq]=${schoolId}&pagination[limit]=-1`),
        ]);
        setSchoolPrograms(programsRes.data?.data || []);
        setSemesters(
          (semRes.data?.data || []).map((el) => ({ label: el.name, value: el.id }))
        );
      } catch (error) {
        console.error(error);
      } finally {
        setFetchingData(false);
      }
    };
    fetchData();
  }, [isOpen, schoolId]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setEnrollmentType('');
      setSelectedProgramType('');
      setSelectedProgram('');
      setSelectedSemester('');
      setSelectedStatus('');
    }
  }, [isOpen]);

  // Get all program types available across school-programs, carrying the kind (grade/technical_career/course)
  const programTypeOptions = React.useMemo(() => {
    const map = {};
    schoolPrograms.forEach((sp) => {
      const attrs = sp.attributes || sp;
      const pt = attrs.program_type?.data || attrs.program_type;
      const ptId = pt?.id || attrs.program_type?.data?.id;
      const ptAttrs = pt?.attributes || pt;
      if (!ptId || !ptAttrs?.name) return;

      const kind = ENROLLMENT_TYPES.find((t) => {
        const program = attrs[t.value]?.data || attrs[t.value];
        return program?.id;
      })?.value;
      if (!kind) return;

      if (!map[ptId]) {
        map[ptId] = { label: ptAttrs.name, value: ptId, kind };
      }
    });
    return Object.values(map);
  }, [schoolPrograms]);

  // Get programs for the selected program type
  const programOptions = React.useMemo(() => {
    if (!enrollmentType || !selectedProgramType) return [];
    const map = {};
    schoolPrograms.forEach((sp) => {
      const attrs = sp.attributes || sp;
      const pt = attrs.program_type?.data || attrs.program_type;
      const ptId = pt?.id || (attrs.program_type?.data?.id);
      if (String(ptId) !== String(selectedProgramType)) return;

      const program = attrs[enrollmentType]?.data || attrs[enrollmentType];
      if (!program || !program.id) return;
      const pAttrs = program.attributes || program;

      if (!map[program.id]) {
        map[program.id] = { label: pAttrs.name, value: program.id };
      }
    });
    return Object.values(map);
  }, [enrollmentType, selectedProgramType, schoolPrograms]);

  const handleProgramTypeChange = (ptId) => {
    setSelectedProgramType(ptId);
    setSelectedProgram('');
    const option = programTypeOptions.find((pt) => String(pt.value) === String(ptId));
    setEnrollmentType(option?.kind || '');
  };

  const handleSubmit = async () => {
    if (!selectedProgram || !selectedSemester || !selectedStatus) {
      toast({
        title: 'Todos los campos son obligatorios',
        status: 'warning',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const typeConfig = ENROLLMENT_TYPES.find((t) => t.value === enrollmentType);
    if (!typeConfig) return;

    try {
      setLoading(true);

      const payload = {
        [enrollmentType]: selectedProgram,
        semester: selectedSemester,
        status: selectedStatus,
        student: studentId,
        program_type: Number(selectedProgramType),
        last_login: dayjs(),
      };

      if (enrollmentType === 'course') {
        payload.progress = 0;
      }

      await xhr.post(`/${typeConfig.endpoint}/`, { data: payload });

      toast({
        title: 'Matricula creada correctamente',
        status: 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });

      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error al crear matricula',
        description: error.response?.data?.error?.message || 'Intenta de nuevo',
        status: 'error',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalCloseButton />
        <ModalHeader>Crear matricula</ModalHeader>
        <ModalBody>
          {fetchingData ? (
            <Box textAlign="center" py={4}>
              <Spinner />
            </Box>
          ) : (
            <>
              <FormControl mb={4}>
                <FormLabel>Tipo de programa</FormLabel>
                <Select
                  placeholder="Seleccionar"
                  value={selectedProgramType}
                  onChange={(e) => handleProgramTypeChange(e.target.value)}
                >
                  {programTypeOptions.map((pt) => (
                    <option key={pt.value} value={pt.value}>
                      {pt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              {selectedProgramType && (
                <FormControl mb={4}>
                  <FormLabel>Programa</FormLabel>
                  <Select
                    placeholder="Seleccionar"
                    value={selectedProgram}
                    onChange={(e) => setSelectedProgram(e.target.value)}
                  >
                    {programOptions.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedProgram && (
                <>
                  <FormControl mb={4}>
                    <FormLabel>Periodo academico</FormLabel>
                    <Select
                      placeholder="Seleccionar"
                      value={selectedSemester}
                      onChange={(e) => setSelectedSemester(e.target.value)}
                    >
                      {semesters.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl mb={4}>
                    <FormLabel>Estado</FormLabel>
                    <Select
                      placeholder="Seleccionar"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="registrado">Registrado</option>
                      <option value="estudiando">Estudiando</option>
                    </Select>
                  </FormControl>
                </>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="purple"
            bg="#574feb"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={!selectedProgram || !selectedSemester || !selectedStatus}
          >
            Crear matricula
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
